import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight } from 'lucide-react';
import { tutorialAPI } from '@/utils/tutorialApi';
import { buildManualNodePath } from '@/config/ipeRoutes';
import '@/styles/ipe/workbench/workbench-manual-link.css';

/**
 * 工作台侧栏「使用教程」入口：全宽卡片或紧凑按钮（链至首篇教程）。
 *
 * @param {string} navId - 工作台 navId，用于拉取关联教程
 * @param {string} [className] - 附加类名
 * @param {'card'|'button'} [variant='card'] - card 为侧栏全宽块；button 为与历史记录并列的紧凑按钮
 */
export default function WorkbenchManualLink({ navId, className = '', variant = 'card' }) {
  const { t } = useTranslation('workbench');
  const [articles, setArticles] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!navId) {
      setArticles([]);
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    tutorialAPI.getByNavId(navId)
      .then((data) => {
        if (!cancelled) setArticles(data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [navId]);

  if (!navId || !loaded || articles.length === 0) return null;

  const primary = articles[0];

  if (variant === 'button') {
    return (
      <Link
        to={buildManualNodePath(primary.slug)}
        target="_blank"
        rel="noopener noreferrer"
        className={`workbench-manual-link-btn ${className}`.trim()}
      >
        <BookOpen size={15} aria-hidden="true" />
        {t('studioChrome.tutorial')}
      </Link>
    );
  }

  return (
    <div className={`workbench-manual-link ${className}`.trim()}>
      <div className="workbench-manual-link__icon" aria-hidden="true">
        <BookOpen size={16} />
      </div>
      <div className="workbench-manual-link__body">
        <div className="workbench-manual-link__title">{t('studioChrome.tutorial')}</div>
        <p className="workbench-manual-link__desc">
          {articles.length === 1
            ? (primary.summary || '查看本功能的操作说明与最佳实践')
            : `已关联 ${articles.length} 篇教程，点击查看`}
        </p>
        <div className="workbench-manual-link__links">
          {articles.slice(0, 3).map((item) => (
            <Link
              key={item.id}
              to={buildManualNodePath(item.slug)}
              target="_blank"
              rel="noopener noreferrer"
              className="workbench-manual-link__item"
            >
              <span>{item.title}</span>
              <ChevronRight size={14} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
